/**
 * Public read-only project viewer (no auth required).
 *
 * Route: /share/:token
 *
 * Renders the project Spec summary, Equipment List, BOM, Cable Schedule, Small
 * Power BOM, and Validation Report — plus a PDF download. Everything is
 * read-only; no mutating API calls are made.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import {
  Lightning, Cube, ListBullets, FilePdf, ShieldCheck, ShieldWarning,
  Power, Lightbulb, ArrowSquareOut,
} from "@phosphor-icons/react";

export default function SharedProjectView() {
  const { token } = useParams();
  const [data, setData] = useState(null);    // full payload | null
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(`${API}/share/${token}`, { credentials: "omit" });
        if (!resp.ok) {
          setError(resp.status === 404 ? "Share link not found or revoked." : "Failed to load shared project.");
          return;
        }
        setData(await resp.json());
      } catch {
        setError("Network error loading shared project.");
      }
    })();
  }, [token]);

  if (error) {
    return (
      <Shell>
        <div className="border border-red-500/40 bg-red-500/5 p-6 text-sm text-red-200 font-mono" data-testid="share-error">
          {error}
        </div>
      </Shell>
    );
  }
  if (!data) {
    return (
      <Shell>
        <div className="text-gray-500 font-mono text-sm p-6" data-testid="share-loading">Loading shared project…</div>
      </Shell>
    );
  }

  const { project: p, bom, small_power_bom: spBom, cable_schedule: cs, validation, distributor_multiplier: dist, share } = data;
  const pdfUrl = `${API}/share/${token}/pdf`;
  const hasMult = !!dist;

  return (
    <Shell>
      {/* Project header */}
      <div className="border-b border-white/10 pb-4 mb-6 flex items-end justify-between flex-wrap gap-3" data-testid="share-header">
        <div>
          <div className="label-tag">{p.code_standard} · {(p.panel_type || "").replace(/_/g, " ")}</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold" data-testid="share-project-name">{p.name}</h1>
          {p.description && <p className="text-sm text-gray-400 mt-1 max-w-3xl">{p.description}</p>}
          <div className="text-[10px] tracking-widest uppercase text-gray-500 mt-3 font-mono">
            shared link · {share.view_count} view{share.view_count === 1 ? "" : "s"} · read-only
          </div>
        </div>
        <a href={pdfUrl} target="_blank" rel="noreferrer"
           className="btn-primary text-sm py-2 inline-flex items-center gap-2"
           data-testid="share-download-pdf">
          <FilePdf size={16} weight="fill" /> Download PDF
        </a>
      </div>

      {/* Spec summary stats */}
      <Section title="Specification" icon={<Lightning size={20} className="text-[#00E5FF]" />} testid="share-spec">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Voltage" value={`${p.voltage}V ${p.phases || "1φ"}`} />
          <Stat label="Main breaker" value={`${p.main_breaker_amps || "—"} A`} />
          <Stat label="Circuits" value={(p.circuits || []).length} />
          <Stat label="Equipment list" value={(p.equipment_list || []).length} />
          <Stat label="Dimensions (in)" value={p.length_in && p.width_in ? `${p.length_in}L × ${p.width_in}W × ${p.depth_in || "?"}D` : "—"} />
          <Stat label="Weight" value={p.weight_lbs ? `${p.weight_lbs} lbs` : "—"} />
          <Stat label="Conductor" value={(p.conductor_material || "copper").toUpperCase()} />
          <Stat label="Location" value={p.location || "—"} />
        </div>
      </Section>

      {/* Equipment list */}
      {(p.equipment_list || []).length > 0 && (
        <Section title={`Equipment list (${p.equipment_list.length})`} icon={<Cube size={20} className="text-[#00E5FF]" />} testid="share-equipment-list">
          <Table head={["Tag", "Type", "L \" ", "W \" ", "D \" ", "Mount \" ", "Notes"]}>
            {p.equipment_list.map((it) => (
              <tr key={it.id} className="border-b border-white/5">
                <td className="p-2 font-mono text-[#00E5FF]">{it.tag || "—"}</td>
                <td className="p-2">{(it.equipment_type || "—").replace(/_/g, " ")}</td>
                <td className="p-2 text-right">{it.length_in ?? "—"}</td>
                <td className="p-2 text-right">{it.width_in ?? "—"}</td>
                <td className="p-2 text-right">{it.depth_in ?? "—"}</td>
                <td className="p-2 text-right">{it.mount_height_in ?? 0}</td>
                <td className="p-2 text-xs text-gray-400">{it.notes || ""}</td>
              </tr>
            ))}
          </Table>
        </Section>
      )}

      {/* Validation */}
      {validation && (
        <Section
          title="Validation"
          icon={validation.compliant
            ? <ShieldCheck size={20} className="text-[#22c55e]" />
            : <ShieldWarning size={20} className="text-yellow-400" />}
          testid="share-validation"
        >
          <div className={`border p-3 mb-3 text-sm font-mono ${validation.compliant ? "border-[#22c55e]/30 bg-[#22c55e]/5 text-[#22c55e]" : "border-yellow-500/30 bg-yellow-500/5 text-yellow-200"}`}>
            {validation.compliant
              ? "Design is compliant with NEC 110.26 working-space rules."
              : `Found ${validation.summary?.errors || 0} error(s) and ${validation.summary?.warnings || 0} warning(s).`}
          </div>
          {(validation.issues || []).length > 0 && (
            <ul className="text-xs font-mono space-y-1">
              {(validation.issues || []).map((i, idx) => (
                <li key={`v-${idx}`} className={i.level === "error" ? "text-red-400" : "text-yellow-300"}>
                  · [{i.level?.toUpperCase()}] {i.tag ? `${i.tag} — ` : ""}{i.message}
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {/* BOM */}
      {bom?.length > 0 && (
        <Section title={`Bill of materials (${bom.length})`} icon={<ListBullets size={20} className="text-[#00E5FF]" />} testid="share-bom">
          <BomTable items={bom} hasMult={hasMult} />
        </Section>
      )}

      {/* Cable schedule */}
      {cs?.cables?.length > 0 && (
        <Section title={`Cable schedule · ${cs.code || "NEC"} · ${(cs.cable_type_default || "").toUpperCase()}`} icon={<ListBullets size={20} className="text-[#00E5FF]" />} testid="share-cables">
          <Table head={["Tag", "Load", "Amps", "Cable", "Conductors", "Conduit", "VD%"]}>
            {cs.cables.map((c) => (
              <tr key={c.id || c.tag} className="border-b border-white/5">
                <td className="p-2 font-mono text-[#00E5FF]">{c.tag || "—"}</td>
                <td className="p-2">{c.load_description || c.equipment_type || "—"}</td>
                <td className="p-2 text-right">{c.amps ?? "—"}</td>
                <td className="p-2">{c.cable_type || "—"}</td>
                <td className="p-2">{c.conductor_size || "—"}</td>
                <td className="p-2">{c.conduit_size_in ? `${c.conduit_size_in}" ${c.conduit_type || "EMT"}` : "—"}</td>
                <td className="p-2 text-right">{c.voltage_drop_pct?.toFixed?.(2) ?? "—"}</td>
              </tr>
            ))}
          </Table>
        </Section>
      )}

      {/* Small Power BOM */}
      {spBom?.length > 0 && (
        <Section title={`Small power BOM (${spBom.length})`} icon={<Lightbulb size={20} className="text-[#FFCC00]" />} testid="share-sp-bom">
          <BomTable items={spBom} hasMult={hasMult} accentColor="#FFCC00" />
        </Section>
      )}

      {/* Small power layout — counts only */}
      {p.small_power?.summary && (
        <Section title="Small power summary" icon={<Power size={20} className="text-[#00E5FF]" />} testid="share-sp-summary">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Receptacles" value={p.small_power.summary.receptacle_count ?? 0} />
            <Stat label="Luminaires"  value={p.small_power.summary.luminaire_count ?? 0} />
            <Stat label="Switches"    value={p.small_power.summary.switch_count ?? 0} />
            <Stat label="Code"        value={p.small_power.code || "—"} />
          </div>
        </Section>
      )}

      <div className="border-t border-white/10 pt-4 mt-10 text-[11px] uppercase tracking-widest text-gray-600 font-mono flex items-center gap-2">
        <span>Powered by</span>
        <a href="/" className="text-[#00E5FF] hover:underline inline-flex items-center gap-1">
          EEngDeS <ArrowSquareOut size={11} />
        </a>
        <span className="text-gray-700">·</span>
        <span>Read-only share — generated by the project owner</span>
      </div>
    </Shell>
  );
}

/* ── shell / atoms ───────────────────────────────────────────────── */

function Shell({ children }) {
  return (
    <div className="industrial min-h-screen bg-[#0A0C10] text-white" data-testid="shared-project-view">
      <div className="border-b border-white/10 bg-[#0d1015]">
        <div className="px-6 md:px-10 py-4 flex items-center gap-3">
          <Lightning size={20} className="text-[#00E5FF]" weight="fill" />
          <div>
            <div className="font-display text-base font-bold">EEngDeS</div>
            <div className="text-[10px] tracking-widest uppercase text-gray-500">Enhanced Engineering Design Studio</div>
          </div>
        </div>
      </div>
      <div className="px-6 md:px-10 py-8 max-w-6xl mx-auto">{children}</div>
    </div>
  );
}

function Section({ title, icon, children, testid }) {
  return (
    <div className="mb-8" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {icon}<h2 className="font-display text-xl font-bold">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#0a0d12] border border-white/10 p-3">
      <div className="label-tag text-[10px]">{label}</div>
      <div className="font-display text-lg font-bold text-[#00E5FF] break-all">{value}</div>
    </div>
  );
}

function Table({ head, children }) {
  return (
    <div className="border border-white/10 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#0a0d12] border-b border-white/10">
          <tr>
            {head.map((h) => (
              <th key={h} className="text-left text-[10px] uppercase tracking-widest text-gray-500 p-2 font-mono">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function BomTable({ items, hasMult, accentColor = "#00E5FF" }) {
  const total = items.reduce((acc, it) => acc + (it.ext_price_usd || 0), 0);
  const netTotal = items.reduce((acc, it) => acc + (it.net_ext_price_usd || 0), 0);
  return (
    <div className="border border-white/10 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#0a0d12] border-b border-white/10">
          <tr>
            {["Part #", "Manufacturer", "Description", "Qty", "Unit", "Ext"].map((h) => (
              <th key={h} className="text-left text-[10px] uppercase tracking-widest text-gray-500 p-2 font-mono">{h}</th>
            ))}
            {hasMult && <th className="text-right text-[10px] uppercase tracking-widest text-[#FFCC00] p-2 font-mono">Net Unit</th>}
            {hasMult && <th className="text-right text-[10px] uppercase tracking-widest text-[#FFCC00] p-2 font-mono">Net Ext</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((i, idx) => (
            <tr key={`${i.part_number}-${idx}`} className="border-b border-white/5">
              <td className="p-2 font-mono" style={{ color: accentColor }}>{i.part_number || "—"}</td>
              <td className="p-2">{i.manufacturer || "—"}</td>
              <td className="p-2">{i.description || "—"}</td>
              <td className="p-2 text-right">{i.quantity}</td>
              <td className="p-2 text-right">${(i.unit_price_usd || 0).toFixed(2)}</td>
              <td className="p-2 text-right">${(i.ext_price_usd || 0).toFixed(2)}</td>
              {hasMult && <td className="p-2 text-right text-[#FFCC00]">${(i.net_unit_price_usd || 0).toFixed(2)}</td>}
              {hasMult && <td className="p-2 text-right text-[#FFCC00]">${(i.net_ext_price_usd || 0).toFixed(2)}</td>}
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-[#0a0d12] border-t border-white/10 font-bold">
          <tr>
            <td colSpan={5} className="p-2 text-right">Total</td>
            <td className="p-2 text-right" style={{ color: accentColor }}>${total.toFixed(2)}</td>
            {hasMult && <td className="p-2"></td>}
            {hasMult && <td className="p-2 text-right text-[#FFCC00]">${netTotal.toFixed(2)}</td>}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
